import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  ListGroup,
  ListGroupItem,
  Nav,
  NavItem,
  UncontrolledDropdown,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Button,
  UncontrolledPopover
} from 'reactstrap';
import { NavLink as NavLinkStrap } from 'reactstrap';
import { User, Bell, FileText, ChevronDown } from 'react-feather';
import { toast } from 'react-toastify';
import { useHistory, NavLink } from 'react-router-dom';
import { signOutUser } from '../../components/auth/authActions';
import { clearDSZPriceNotification } from '../../components/myAccount/profileActions.js';
import clickFrenzyLogo from '../../assets/images/Sofortig_Notification_ClickFrenzy_380x80.png';
import { useSsoAuth } from '../../hooks/useSsoAuth';
import usersvg from '../../assets/images/icon/user_purple.svg';
import { downloadByLink } from '../../utils/download';
import manualPriceReviewZip from '../../assets/files/NZ_Shipping_Update_18092026.zip';
import {
  getPromotionBannerSvc,
  getPromotionDownloadLinkSvc,
  getPriceReviewBannerSvc,
  getPriceReviewDownloadLinkSvc
} from '../../services/banner';
import { useFeatureFlagStore } from '../../hooks/store/useFeatureFlagStore';
import moment from 'moment-timezone';
import { showCustomToast } from '../../controls/CustomToast';
import './headerUserbox.scss';

/** Manually bundled row appended after the API-driven price review list. */
const INCLUDE_MANUAL_PRICE_REVIEW_ROW = false;
const MANUAL_PRICE_REVIEW_ROW = {
  rowText: 'NZ Shipping Unavailable for 11 SKUs, Effective 18 Sept 2026',
  downloadFilename: 'NZ_Shipping_Update_18092026.zip'
};

/** API `effective_time` may be wrong for this banner; show this instant instead (+10). */
const PRICE_REVIEW_EFFECTIVE_OVERRIDE_BY_ID = {
  '251626557560298322': '2026-04-23T00:00:00+10:00'
};

export function HeaderUserbox() {
  const dispatch = useDispatch();
  const { showFeatureItems5 } = useSelector((state) => state.ThemeOptions);
  const { shopname, email, dszEmail, token } = useSelector(
    (state) => state.auth
  );
  const { DszNotificationFlag } = useSelector((state) => state.profile);
  const history = useHistory();
  const isClickFrenzy = process.env.REACT_APP_IS_CLICK_FRENZY;
  const [enableDropDownOpen, setEnableDropDownOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [promotionBanners, setPromotionBanners] = useState([]);
  const [isLoadingPromotion, setIsLoadingPromotion] = useState(false);
  const [priceReviewBanners, setPriceReviewBanners] = useState([]);
  const [isLoadingPriceReview, setIsLoadingPriceReview] = useState(false);
  const [openFileBannerDropdown, setOpenFileBannerDropdown] = useState(null);

  const enableButtonToggle = () =>
    setEnableDropDownOpen((prevState) => !prevState);

  const { ssoEnabled, ssoLogout } = useSsoAuth(dszEmail, token);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth > 1600);
      setIsMediumScreen(window.innerWidth > 1400 && window.innerWidth <= 1600);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  useEffect(() => {
    const fetchPromotionBanners = async () => {
      try {
        setIsLoadingPromotion(true);
        const response = await getPromotionBannerSvc();
        if (response.success && response.data) {
          setPromotionBanners(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch promotion banners:', error);
        setPromotionBanners([]);
      } finally {
        setIsLoadingPromotion(false);
      }
    };

    fetchPromotionBanners();
  }, []);

  useEffect(() => {
    const fetchPriceReviewBanner = async () => {
      try {
        setIsLoadingPriceReview(true);
        const response = await getPriceReviewBannerSvc();
        if (response.success && response.data) {
          setPriceReviewBanners(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch price review banner:', error);
        setPriceReviewBanners([]);
      } finally {
        setIsLoadingPriceReview(false);
      }
    };

    fetchPriceReviewBanner();
  }, []);

  async function handleSignOut(e) {
    e.preventDefault();
    console.log('start handleSignOut');
    try {
      if (ssoEnabled) {
        const ssoLogoutRes = await ssoLogout();
        if (ssoLogoutRes) {
          const signout = await dispatch(signOutUser());
          if (signout) {
            history.push('/login');
          }
        } else {
          toast.error('sso logout failed, please retry later');
        }
      } else {
        const signout = await dispatch(signOutUser());
        if (signout) {
          localStorage.setItem('forceRefresh', 'true');
          history.push('/login');
        }
      }
    } catch (error) {
      toast.error(error.message);
    }
  }

  const clearDszNotification = () => {
    dispatch(clearDSZPriceNotification());
  };

  const handleDownloadFile = (file, filename) => {
    downloadByLink(file, filename);
  };

  const handlePromotionDownload = async (eventId) => {
    try {
      setIsLoadingPromotion(true);
      const response = await getPromotionDownloadLinkSvc(eventId);
      if (response.success && response.data && response.data.download_url) {
        // Open the download URL directly
        window.location.href = response.data.download_url;
      } else {
        showCustomToast('Failed to download promotion file');
      }
    } catch (error) {
      console.error('Failed to download promotion file:', error);
      showCustomToast('Failed to download promotion file');
    } finally {
      setIsLoadingPromotion(false);
    }
  };

  const handlePriceReviewDownload = async (priceReviewId) => {
    try {
      setIsLoadingPriceReview(true);
      const response = await getPriceReviewDownloadLinkSvc(priceReviewId);
      if (response.success && response.data && response.data.download_url) {
        window.location.href = response.data.download_url;
      } else {
        showCustomToast('Failed to download price review summary file');
      }
    } catch (error) {
      console.error('Failed to download price review summary file:', error);
      showCustomToast('Failed to download price review summary file');
    } finally {
      setIsLoadingPriceReview(false);
    }
  };

  /** Compact range for dropdown rows, e.g. (22 Mar - 31 Mar 2026) */
  const formatPromotionPeriodParen = (startDateStr, endDateStr) => {
    const extractDatePart = (dateStr) => {
      if (!dateStr) return '';
      const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1];
      }
      return dateStr;
    };

    const startDatePart = extractDatePart(startDateStr);
    const endDatePart = extractDatePart(endDateStr);
    const startDate = moment(startDatePart, 'YYYY-MM-DD');
    const endDate = moment(endDatePart, 'YYYY-MM-DD');

    if (startDate.year() === endDate.year()) {
      return `(${startDate.format('D MMM')} - ${endDate.format('D MMM YYYY')})`;
    }
    return `(${startDate.format('D MMM YYYY')} - ${endDate.format(
      'D MMM YYYY'
    )})`;
  };

  /** Calendar date only, e.g. `30 Apr 2026` (English month, no leading zero on day). */
  const formatPriceReviewEffectiveDate = (effectiveTime) => {
    if (!effectiveTime) return '';
    const s = String(effectiveTime).trim();
    const date = s.includes('T')
      ? moment.parseZone(s)
      : moment(s, 'YYYY-MM-DD');
    if (!date.isValid()) return '';
    return date.clone().locale('en-au').format('D MMM YYYY');
  };

  /** Full segment after the em dash, e.g. `Effective: 30 Apr 2026`. */
  const formatPriceReviewEffectiveLabel = (effectiveTime) => {
    const d = formatPriceReviewEffectiveDate(effectiveTime);
    return d ? `Effective: ${d}` : '';
  };

  const priceReviewEffectiveForDisplay = (banner) =>
    PRICE_REVIEW_EFFECTIVE_OVERRIDE_BY_ID[String(banner?.id)] ??
    banner?.effective_time;

  const hasPriceReview =
    priceReviewBanners.length > 0 || INCLUDE_MANUAL_PRICE_REVIEW_ROW;
  const hasPromotion = promotionBanners.length > 0;

  const headerUserboxLayoutClass =
    !hasPriceReview && !hasPromotion
      ? 'header-userbox--user-only'
      : hasPriceReview && hasPromotion
      ? 'header-userbox--three-cols'
      : 'header-userbox--banner-and-user';

  return (
    <>
      <div className={clsx('header-userbox', headerUserboxLayoutClass)}>
        {hasPriceReview && (
          <div className="header-userbox__banner-slot">
            <Dropdown
              className="header-userbox__dropdown"
              isOpen={openFileBannerDropdown === 'priceReview'}
              toggle={() =>
                setOpenFileBannerDropdown((prev) =>
                  prev === 'priceReview' ? null : 'priceReview'
                )
              }>
              <DropdownToggle
                tag="div"
                caret={false}
                className="header-userbox__dropdown-toggle"
                aria-expanded={openFileBannerDropdown === 'priceReview'}
                aria-haspopup="true">
                <div className="header-userbox__icon-circle">
                  <span className="header-userbox__icon-text">i</span>
                </div>
                <span className="header-userbox__toggle-label">
                  New Price Review File Now Available
                </span>
                <ChevronDown
                  size={16}
                  className="header-userbox__caret"
                  aria-hidden
                />
              </DropdownToggle>
              <DropdownMenu className="header-userbox__dropdown-menu">
                {priceReviewBanners.map((priceReviewBanner) => (
                  <div
                    key={priceReviewBanner.id}
                    className="header-userbox__menu-row">
                    <FileText
                      size={20}
                      strokeWidth={1.75}
                      className="header-userbox__file-icon"
                      aria-hidden
                    />
                    <span className="header-userbox__row-text">
                      New Price Review File Now Available –{' '}
                      {formatPriceReviewEffectiveLabel(
                        priceReviewEffectiveForDisplay(priceReviewBanner)
                      )}
                    </span>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePriceReviewDownload(priceReviewBanner.id);
                      }}
                      className={
                        'header-userbox__download-link' +
                        (isLoadingPriceReview
                          ? ' header-userbox__download-link--busy'
                          : '')
                      }>
                      Download Now
                    </a>
                  </div>
                ))}
                {INCLUDE_MANUAL_PRICE_REVIEW_ROW && (
                  <div className="header-userbox__menu-row">
                    <FileText
                      size={20}
                      strokeWidth={1.75}
                      className="header-userbox__file-icon"
                      aria-hidden
                    />
                    <span className="header-userbox__row-text">
                      {MANUAL_PRICE_REVIEW_ROW.rowText}
                    </span>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadFile(
                          manualPriceReviewZip,
                          MANUAL_PRICE_REVIEW_ROW.downloadFilename
                        );
                      }}
                      className="header-userbox__download-link">
                      Download Now
                    </a>
                  </div>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        {hasPromotion && (
          <div className="header-userbox__banner-slot">
            <Dropdown
              className="header-userbox__dropdown"
              isOpen={openFileBannerDropdown === 'promotion'}
              toggle={() =>
                setOpenFileBannerDropdown((prev) =>
                  prev === 'promotion' ? null : 'promotion'
                )
              }>
              <DropdownToggle
                tag="div"
                caret={false}
                className="header-userbox__dropdown-toggle"
                aria-expanded={openFileBannerDropdown === 'promotion'}
                aria-haspopup="true">
                <div className="header-userbox__icon-circle">
                  <span className="header-userbox__icon-text">i</span>
                </div>
                <span className="header-userbox__toggle-label">
                  New Promotion File Now Available
                </span>
                <ChevronDown
                  size={16}
                  className="header-userbox__caret"
                  aria-hidden
                />
              </DropdownToggle>
              <DropdownMenu className="header-userbox__dropdown-menu">
                {promotionBanners.map((banner) => (
                  <div key={banner.id} className="header-userbox__menu-row">
                    <FileText
                      size={20}
                      strokeWidth={1.75}
                      className="header-userbox__file-icon"
                      aria-hidden
                    />
                    <span className="header-userbox__row-text">
                      {banner.title ? `${banner.title} – ` : 'New Promotion File Now Available – '}
                      {formatPromotionPeriodParen(
                        banner.effective_date,
                        banner.end_date
                      )}
                    </span>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePromotionDownload(banner.id);
                      }}
                      className={
                        'header-userbox__download-link' +
                        (isLoadingPromotion
                          ? ' header-userbox__download-link--busy'
                          : '')
                      }>
                      Download Now
                    </a>
                  </div>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        {/* User Info */}
        <div className="header-userbox__user-area">
          <div className="header-userbox__user-row">
            <img src={usersvg} alt="user icon"></img>
            <span className="header-userbox__email">{dszEmail}</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default HeaderUserbox;
